import { Dialog as RadixDialog } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface SheetProps {
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	trigger?: ReactNode;
	title: ReactNode;
	description?: ReactNode;
	footer?: ReactNode;
	children?: ReactNode;
	className?: string;
	/** `bottom` — короткие формы; `left` — мобильное меню витрины. */
	side?: "bottom" | "left";
	/**
	 * Спрятать заголовок визуально, оставив его доступным именем диалога.
	 *
	 * Radix требует `Title` всегда, но в навигационном ящике из семи строк с
	 * иконками подпись «Разделы витрины» ничего не добавляет и съедает первый
	 * экран (замечание владельца 22.08.2026).
	 */
	titleHidden?: boolean;
}

/**
 * Мобильная шторка. Снизу — то же содержимое, что у Dialog, с ручкой 32×4.
 * Слева — навигационный ящик: без ручки, на всю высоту, скругляется справа.
 *
 * Заголовок обязателен и всегда в разметке: Radix иначе ругается на диалог без
 * доступного имени. Если он не нужен визуально, прячьте его классом, а не удаляйте.
 */
export function Sheet({
	open,
	defaultOpen,
	onOpenChange,
	trigger,
	title,
	description,
	footer,
	children,
	className,
	side = "bottom",
	titleHidden = false,
}: SheetProps) {
	const fromLeft = side === "left";

	return (
		<RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
			{trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
			<RadixDialog.Portal>
				<RadixDialog.Overlay
					className={cn(
						"fixed inset-0 z-120 bg-scrim backdrop-blur-[8px]",
						"data-[state=open]:[animation:iec-scrim-in_var(--dur-300)_var(--ease-smooth)]",
					)}
				/>
				<RadixDialog.Content
					className={cn(
						"z-120 flex flex-col bg-card shadow-float outline-none",
						fromLeft
							? [
									"fixed inset-y-0 left-0 w-[min(20rem,88vw)] rounded-r-card",
									"data-[state=open]:[animation:iec-drawer-in_var(--dur-450)_var(--ease-elegant)]",
									"data-[state=closed]:[animation:iec-drawer-out_var(--dur-react)_var(--ease-smooth)]",
								]
							: [
									"fixed inset-x-0 bottom-0 max-h-[90dvh] rounded-t-card",
									"data-[state=open]:[animation:iec-sheet-in_var(--dur-450)_var(--ease-elegant)]",
									"data-[state=closed]:[animation:iec-sheet-out_var(--dur-react)_var(--ease-smooth)]",
								],
						className,
					)}
				>
					{fromLeft ? null : (
						<span
							aria-hidden="true"
							className="mx-auto my-3 block h-1 w-8 rounded-full bg-black/10"
						/>
					)}
					<div
						className={cn("flex min-h-0 flex-1 flex-col gap-4 px-4 pb-4", fromLeft ? "pt-5" : null)}
					>
						<div className={cn("flex flex-col gap-1.5", titleHidden && "sr-only")}>
							<RadixDialog.Title className="t-subsection">{title}</RadixDialog.Title>
							{description != null ? (
								<RadixDialog.Description className="t-body text-fg-muted">
									{description}
								</RadixDialog.Description>
							) : null}
						</div>
						<div className="iec-scroll flex min-h-0 flex-1 flex-col gap-4">{children}</div>
						{footer != null ? <div className="flex gap-1">{footer}</div> : null}
					</div>
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	);
}

export const SheetClose = RadixDialog.Close;
